import React, { useState, useEffect } from 'react';
import { taskAPI, clientAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Search, Filter, Download, Calendar, User, Building2, FileText } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const AllTasksReport = () => {
  const { isPartner, isManager } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksRes, clientsRes] = await Promise.all([
        taskAPI.getAll({}),
        clientAPI.getAll(),
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter((task) => {
    // Search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        task.client_name?.toLowerCase().includes(search) ||
        task.service_name?.toLowerCase().includes(search) ||
        task.task_type_name?.toLowerCase().includes(search) ||
        task.assigned_to_name?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Client filter
    if (filterClient && task.client_id !== filterClient) return false;

    // Status filter
    if (filterStatus && task.status !== filterStatus) return false;

    // Priority filter
    if (filterPriority && task.priority !== filterPriority) return false;

    // Date range filter
    if (filterDateFrom) {
      const taskDate = new Date(task.due_date);
      const fromDate = new Date(filterDateFrom);
      if (taskDate < fromDate) return false;
    }
    if (filterDateTo) {
      const taskDate = new Date(task.due_date);
      const toDate = new Date(filterDateTo);
      if (taskDate > toDate) return false;
    }

    return true;
  });

  const exportToExcel = () => {
    // Create CSV content
    const headers = [
      'Client',
      'Service',
      'Task Type',
      'Assigned To',
      'Due Date',
      'Priority',
      'Status',
      'Invoice',
      'Receipt',
    ];
    
    const rows = filteredTasks.map(task => [
      task.client_name,
      task.service_name,
      task.task_type_name,
      task.assigned_to_name,
      formatDate(task.due_date),
      task.priority,
      task.status,
      task.invoice ? `₹${task.invoice.amount}` : '-',
      task.receipt ? `₹${task.receipt.amount}` : '-',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-tasks-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterClient('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const getStatusBadge = (status) => {
    const colors = {
      'To Do': 'bg-gray-100 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Completed': 'bg-green-100 text-green-700',
      'Overdue': 'bg-red-100 text-red-700',
    };
    return colors[status] || colors['To Do'];
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'bg-green-100 text-green-700',
      'Medium': 'bg-yellow-100 text-yellow-700',
      'High': 'bg-red-100 text-red-700',
    };
    return colors[priority] || colors['Medium'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">All Tasks Report</h2>
        <button
          onClick={exportToExcel}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          data-testid="export-tasks-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium mb-1">
              <Search className="w-4 h-4 inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client, service, task type, or staff..."
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="search-tasks-input"
            />
          </div>

          {/* Client Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Overdue">Overdue</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Priorities</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium mb-1">Due Date From</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium mb-1">Due Date To</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </p>
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="font-medium">{task.client_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{task.service_name}</td>
                  <td className="px-4 py-3 text-sm">{task.task_type_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 mr-1 text-gray-400" />
                      {task.assigned_to_name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(task.due_date)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${getPriorityBadge(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(task.status)}`}>
                      {task.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {task.invoice ? (
                      <span className="text-green-600 font-medium">
                        ₹{task.invoice.amount.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {task.receipt ? (
                      <span className="text-blue-600 font-medium">
                        ₹{task.receipt.amount.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No tasks found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTasksReport;
